export const getApiData = (response) =>
  response?.data?.data || response?.data?.Data || response?.data || null;

export const getApiMessage = (error, fallback = "Something went wrong.") =>
  error?.response?.data?.message ||
  error?.response?.data?.Message ||
  error?.response?.data?.title ||
  error?.message ||
  fallback;

const normalizeKey = (value) => String(value ?? "").trim();

const BILL_STATUS_BY_NUMBER = {
  0: "NotGenerated",
  1: "Draft",
  2: "Issued",
  3: "PartiallyPaid",
  4: "Paid",
  5: "Cancelled",
};

const PAYMENT_METHOD_BY_NUMBER = {
  1: "Cash",
  2: "Card",
  3: "BankTransfer",
  4: "Online",
  5: "POS",
  6: "Other",
};

const LINE_ITEM_TYPE_BY_NUMBER = {
  1: "RoomNight",
  2: "Amenity",
  3: "Activity",
  4: "POSOrder",
  5: "Service",
  6: "ManualCharge",
  7: "Discount",
  8: "Tax",
  9: "ServiceCharge",
  10: "Adjustment",
};

const LINE_ITEM_TYPE_LABELS = {
  RoomNight: "Room charge",
  Amenity: "Amenity",
  Activity: "Activity",
  POSOrder: "Food order",
  Service: "Service",
  ManualCharge: "Manual charge",
  Discount: "Discount",
  Tax: "Tax",
  ServiceCharge: "Service charge",
  Adjustment: "Adjustment",
};

const PAYMENT_METHOD_LABELS = {
  BankTransfer: "Bank transfer",
};

export const getLineItemDisplayTotal = (item, currencyCode = "NPR") => {
  if (item?.isInformational) {
    return `${formatMoney(item.unitPrice, currencyCode)} paid`;
  }

  return formatMoney(item?.lineTotal, currencyCode);
};

export const normalizeBillStatus = (status) => {
  const value = normalizeKey(status);
  if (value in BILL_STATUS_BY_NUMBER) return BILL_STATUS_BY_NUMBER[value];
  return value || "Draft";
};

export const formatBillStatus = (status) => {
  const value = normalizeBillStatus(status);
  return value
    .replace(/([a-z])([A-Z])/g, "$1 $2")
    .replace(/_/g, " ");
};

export const getBillStatusBadgeClass = (status) => {
  switch (normalizeBillStatus(status)) {
    case "Paid":
      return "border-emerald-200 bg-emerald-50 text-emerald-700";
    case "PartiallyPaid":
      return "border-amber-200 bg-amber-50 text-amber-800";
    case "Issued":
      return "border-blue-200 bg-blue-50 text-blue-700";
    case "Cancelled":
      return "border-red-200 bg-red-50 text-red-700";
    case "NotGenerated":
      return "border-slate-200 bg-slate-50 text-slate-600";
    case "Draft":
    default:
      return "border-slate-200 bg-slate-100 text-slate-700";
  }
};

export const formatMoney = (amount, currencyCode = "NPR") => {
  const numeric = Number(amount ?? 0);
  const safeAmount = Number.isFinite(numeric) ? numeric : 0;
  return `${currencyCode || "NPR"} ${safeAmount.toFixed(2)}`;
};

export const formatDateTime = (value) => {
  if (!value) return "—";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return value;
  return date.toLocaleString();
};

export const formatDate = (value) => {
  if (!value) return "—";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return value;
  return date.toLocaleDateString();
};

export const formatPaymentMethod = (method) => {
  const value = normalizeKey(method);
  const normalized = value in PAYMENT_METHOD_BY_NUMBER ? PAYMENT_METHOD_BY_NUMBER[value] : value;
  return (PAYMENT_METHOD_LABELS[normalized] || normalized || "Other")
    .replace(/([a-z])([A-Z])/g, "$1 $2")
    .replace(/_/g, " ");
};

export const formatLineItemType = (type) => {
  const value = normalizeKey(type);
  const normalized = value in LINE_ITEM_TYPE_BY_NUMBER ? LINE_ITEM_TYPE_BY_NUMBER[value] : value;
  return (LINE_ITEM_TYPE_LABELS[normalized] || normalized || "Charge")
    .replace(/([a-z])([A-Z])/g, "$1 $2")
    .replace(/_/g, " ");
};

export const normalizeBill = (bill = {}) => {
  const lineItems = bill.lineItems || bill.LineItems || [];
  const payments = bill.payments || bill.Payments || [];

  return {
    id: bill.id || bill.Id,
    organizationId: bill.organizationId || bill.OrganizationId,
    guestSessionId: bill.guestSessionId || bill.GuestSessionId,
    guestProfileId: bill.guestProfileId || bill.GuestProfileId,
    roomId: bill.roomId || bill.RoomId,
    billNumber: bill.billNumber || bill.BillNumber || "Draft bill",
    status: normalizeBillStatus(bill.status ?? bill.Status),
    guestName: bill.guestName || bill.GuestName || "Guest not provided",
    guestPhone: bill.guestPhone || bill.GuestPhone || "",
    guestEmail: bill.guestEmail || bill.GuestEmail || "",
    roomName: bill.roomName || bill.RoomName || bill.roomNumber || bill.RoomNumber || "—",
    roomNumber: bill.roomNumber || bill.RoomNumber || bill.roomName || bill.RoomName || "—",
    checkInAtUtc: bill.checkInAtUtc || bill.CheckInAtUtc,
    checkOutAtUtc: bill.checkOutAtUtc || bill.CheckOutAtUtc,
    billGeneratedAtUtc: bill.billGeneratedAtUtc || bill.BillGeneratedAtUtc,
    issuedAtUtc: bill.issuedAtUtc || bill.IssuedAtUtc,
    paidAtUtc: bill.paidAtUtc || bill.PaidAtUtc,
    currencyCode: bill.currencyCode || bill.CurrencyCode || "NPR",
    subTotal: Number(bill.subTotal ?? bill.SubTotal ?? 0),
    discountTotal: Number(bill.discountTotal ?? bill.DiscountTotal ?? 0),
    taxTotal: Number(bill.taxTotal ?? bill.TaxTotal ?? 0),
    serviceChargeTotal: Number(bill.serviceChargeTotal ?? bill.ServiceChargeTotal ?? 0),
    grandTotal: Number(bill.grandTotal ?? bill.GrandTotal ?? 0),
    paidAmount: Number(bill.paidAmount ?? bill.PaidAmount ?? 0),
    dueAmount: Number(bill.dueAmount ?? bill.DueAmount ?? 0),
    notes: bill.notes || bill.Notes || "",
    lineItems: Array.isArray(lineItems) ? lineItems.map(normalizeBillLineItem) : [],
    payments: Array.isArray(payments) ? payments.map(normalizeBillPayment) : [],
  };
};

export const normalizeBillLineItem = (item = {}) => ({
  id: item.id || item.Id,
  itemType: item.itemType ?? item.ItemType,
  sourceKey: item.sourceKey || item.SourceKey || "",
  sourceType: item.sourceType || item.SourceType || "",
  sourceId: item.sourceId || item.SourceId,
  isGenerated: item.isGenerated ?? item.IsGenerated ?? false,
  generatedBy: item.generatedBy || item.GeneratedBy || "",
  affectsDueAmount: item.affectsDueAmount ?? item.AffectsDueAmount ?? true,
  description: item.description || item.Description || "Charge",
  quantity: Number(item.quantity ?? item.Quantity ?? 0),
  unitPrice: Number(item.unitPrice ?? item.UnitPrice ?? 0),
  discountAmount: Number(item.discountAmount ?? item.DiscountAmount ?? 0),
  taxAmount: Number(item.taxAmount ?? item.TaxAmount ?? 0),
  serviceChargeAmount: Number(item.serviceChargeAmount ?? item.ServiceChargeAmount ?? 0),
  lineTotal: Number(item.lineTotal ?? item.LineTotal ?? 0),
  revenueAccountCode: item.revenueAccountCode || item.RevenueAccountCode || "",
  revenueAccountName: item.revenueAccountName || item.RevenueAccountName || "",
  chargeDateUtc: item.chargeDateUtc || item.ChargeDateUtc,
  isInformational: item.isInformational ?? item.IsInformational ?? false,
  paidExternallyAtUtc: item.paidExternallyAtUtc || item.PaidExternallyAtUtc,
});

export const formatBillingSourceType = (value) => {
  const normalized = String(value || "").trim();
  const map = {
    RoomCharge: "Room charge",
    POSOrder: "Food order",
    ActivityBooking: "Activity booking",
    ManualCharge: "Manual charge",
    Adjustment: "Adjustment",
    PackagePurchase: "Package purchase",
    EventCouponRedemption: "Event coupon redemption",
    ServiceCharge: "Service charge",
    Payment: "Payment",
    GuestBill: "Guest bill",
  };
  return map[normalized] || normalized.replace(/([a-z])([A-Z])/g, "$1 $2") || "Manual";
};

export const normalizeBillPayment = (payment = {}) => ({
  id: payment.id || payment.Id,
  amount: Number(payment.amount ?? payment.Amount ?? 0),
  paymentMethod: payment.paymentMethod ?? payment.PaymentMethod,
  referenceNumber: payment.referenceNumber || payment.ReferenceNumber || "",
  paidAtUtc: payment.paidAtUtc || payment.PaidAtUtc,
  receivedByUserId: payment.receivedByUserId || payment.ReceivedByUserId,
  notes: payment.notes || payment.Notes || "",
  isVoided: payment.isVoided ?? payment.IsVoided ?? false,
  voidedAtUtc: payment.voidedAtUtc || payment.VoidedAtUtc,
  voidReason: payment.voidReason || payment.VoidReason || "",
});

export const normalizePagedBills = (response, fallbackPage = 1, fallbackPageSize = 20) => {
  const data = getApiData(response) || {};
  const nested = data.data || data.Data;
  const items =
    data.items ||
    data.Items ||
    data.records ||
    data.Records ||
    data.results ||
    data.Results ||
    nested?.items ||
    nested?.Items ||
    (Array.isArray(nested) ? nested : Array.isArray(data) ? data : []);

  const totalRecords =
    data.totalRecords ||
    data.TotalRecords ||
    data.totalCount ||
    data.TotalCount ||
    items.length;

  const pageNumber = data.pageNumber || data.PageNumber || fallbackPage;
  const pageSize = data.pageSize || data.PageSize || fallbackPageSize;
  const totalPages =
    data.totalPages || data.TotalPages || Math.max(1, Math.ceil(totalRecords / pageSize));

  return {
    items: Array.isArray(items) ? items.map(normalizeBill) : [],
    totalRecords,
    pageNumber,
    pageSize,
    totalPages,
  };
};
